import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Badge,
	Box,
	Button,
	Divider,
	Flex,
	Grid,
	Heading,
	HStack,
	OrderedList,
	ListItem,
	Input,
	Text,
	Textarea,
	Tabs,
	TabList,
	TabPanels,
	TabPanel,
	Tab,
	useToast,
	RadioGroup,
	Radio,
	Stack,
	Select,
	TableContainer,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	InputGroup,
	InputRightAddon,
	Checkbox,
} from "@chakra-ui/react";

import issbAPI from "api/issb/issb";
import { dedupe, modifiedUrl } from "utils";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { issbKeys, useSaveAnswer } from "hooks/query";
import { showErrorToast, showInfoToast } from "utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch, useSelector } from "react-redux";
import { AButton } from "../../../components/A";
import { getAIContext, setAIContext } from "../../../api/ai/ai.service";
import MarkdownText from "components/MarkdownText";
import { routes } from "../../../config";
import AnswerActionButtons from "./components/tools/AnswerActionButtons";
import IssbContainer from "../IssbContainer";
import FileListWithBrowseOrUpload from "../components/FileListWithBrowseOrUpload";
import fileinfoService from "api/common/fileinfo.service";
import { setAIEventListener } from "api/ai/ai.service";
import { getStatusFromActivity } from "./components/AnswerActivityOneEvent";
import { useAuth } from "context";
import { useQuery, useQueryClient } from "react-query";
import { getFolderForRound } from "api/common/fileinfo";
import { useRightSidebar } from "../../../context/RightSidebarContext";
import LocalSpinner from "../../../components/LocalSpinner";
import useAirUserRoles from "../../../utils/AuthUtils";
import assert from "utils/assert";
import Issb from "api/issb/issb";
import questionService from "api/issb/question.service";
import { Link as LinkIcon, Sparkle } from "@phosphor-icons/react";
import { ActivityDetailsSources, ActivityDetailsUpdateAnswer } from "./components/AnswerActivityOneEvent";
import { formatDate } from "utils";
import AnswerDetailManagement from "./AnswerDetailManagement";
import RichTextEditor from "./components/RichTextEditor/RichTextEditor";
import MetricInput from "components/MetricInput";
import { isMultipartQuestion } from "./helper";
import { QAPartType } from "./QAPart";
import QuestionPartsList from "./QuestionPartsList";
import { checkCondition, getConditionFilter, prettyPrintCondition, isUnset } from "./check-condition";
import { useGetQuestionsOfRound } from "hooks/query/issb/issb";
import AnswerExclusionCard from "./components/AnswerExclusionCard";
import { H3, H4 } from "components/text/headings";
import QuestionCard from "./components/QuestionCard";
import AIDraftTab from "./AIDraftTab";
import AIAssistPanel from "../components/AIAssistPanel/AIAssistPanel";
import { useContextualPrompts } from "hooks/query/prompt/useContextualPrompts";
import { colors } from "theme/foundations/colors";
import { markdownToHtml, toArray } from "utils/text-utils";
import { useRerender } from "rerenderer";
import { isCDPQuestion } from "../cdp-utils";
import { space } from "utils/text-utils";
import { isExcludedFn } from "api/issb/Question";
import { ACTION_applyAnswer } from "api/ai/ai.service";
import SecondaryHeader from "../components/SecondaryHeader";


export function getLatestAIDraft(activities) {
	if (!activities) {
		return null;
	}
	// AI answer activities
	let aiDraftActivities = activities?.filter((activity) => activity.byAI && activity.type === "UPDATE_ANSWER");
	// filter out no-answer-found (aiSuccess is false)
	aiDraftActivities = aiDraftActivities?.filter((activity) => activity.aiSuccess);
	// take the most recent, trusting that the activities are time sorted
	const _aiDraftActivity = aiDraftActivities?.[0];
	return _aiDraftActivity;
}

/**
 * One question and answer

Note: This is a key page ... still, the amount of code here, and the plumbing to pass info down from here to the sub-components, is a lot.
There's no easy way to simplify this -- and it has several separate aspects.
Suggestions: 
 - Refactor to have a headless data layer for question/answer data, which handles server-side data fetching, caching, and local state management.
 - Move more components to separate files.

 */
export default function AnswerDetailPage() {
	// Note: manage the tab index, so we can tell when an AI draft is read
	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const activeTabText =
		activeTabIndex === 0
			? "Answer (the User-edited Disclosure answer), not the AI_draft"
			: "AI_draft (AI Draft Answer)";
	const [thisQuestionUrl] = useState(window.location.href); // stash the url, to avoid being confused during unmount-and-navigate-away
	useEffect(() => {
		// update AI context so the AI assistant knows which tab you are on
		let aiContext = getAIContext();
		aiContext.activeTab = activeTabText;
		setAIContext(aiContext, thisQuestionUrl);
	}, [activeTabIndex, activeTabText, thisQuestionUrl]);

	const { getAccessTokenSilently } = useAuth();
	const queryClient = useQueryClient();

	const { isAssigned } = useAirUserRoles();

	const toast = useToast();

	const [searchParams] = useSearchParams();
	const questionId = searchParams.get("id");

  useEffect(() => {
    setAnswerModifiedFlag(false);
  }, [questionId]);

	const [answerModifiedFlag, setAnswerModifiedFlag] = React.useState(false);
	const { isOpen, setRightSidebar, activationId, doOpen } = useRightSidebar();

	// q round id
	const roundSelector = useSelector((state) => state.issbRound);
	const round = roundSelector.selectedRound;
	const questionnaireId = round?.id;

	// fetch the question/answer data
	const { data: questionData } = useQuery(
		[...issbKeys.answer(), questionId],
		() => getAccessTokenSilently().then(() => questionService.get(questionId)),
		{
			onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
			cacheTime: 0,
			enabled: !!questionId,
		}
	);


  const { rerender } = useRerender();

	// Note: useCallback is used here and below to keep lint happy (with useEffect dependencies) without triggering useEffect every render
	const setCurrentAnswer = useCallback(
		(answerString) => {
			console.log("setCurrentAnswer", answerString, "replaces", questionData.answer);
			assert(
				!answerString || typeof answerString === "string",
				"setCurrentAnswer answer must be a string",
				answerString
			);
			// modified flag
			if (questionData.answer !== answerString) {
				setAnswerModifiedFlag(true);
			}
			// update the answer
			questionData.answer = answerString;
			// update answer in AI context
			let aiContext = getAIContext();
			setAIContext({
				...aiContext,
				answer: answerString,
			});
			rerender();

			// Optimistically update the React Query cache for this question.
			const key = [...issbKeys.answer(), questionId];
			queryClient.setQueryData(key, (old) => {
				if (!old) return old;
				return { ...old, answer: answerString };
			});
		},
		[questionData, rerender, queryClient, questionId]
	);

	// copy-pasta from AdditionalStatuses.jsx. Called here by applyActivity.
	const handleChange = useCallback(
		(field, status) => {
			assert(field && status);
			const dataToSubmit = {
				questionIds: [questionId],
				field,
				status,
			};
			// update the local object (Note: rather than invalidate the query)
			questionData[field] = status;
			// Optimistically update the query cache so UI updates immediately
			const key = [...issbKeys.answer(), questionId];
			queryClient.setQueryData(key, (old) => {
				if (!old) return old;
				return { ...old, [field]: status };
			});

			return (
				Issb.changeStatus(dataToSubmit)
					//   .then(() => { // triggers loss of other edits (see Jira ESG-2678)
					//     queryClient.invalidateQueries(issbKeys.answer());
					//   })
					.catch((err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message))
			);
		},
		[questionId, toast, questionData, queryClient]
	);

	// AI draft activities
	const [aiDraftActivity, setAiDraftActivity] = useState();
	const {
		data: activities,
		isLoading,
		error,
	} = useQuery(
		[issbKeys.activities(), questionData?.id],
		() => getAccessTokenSilently().then(() => issbAPI.getActivities({ id: questionId })),
		{
			enabled: !!questionId,
			onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
		}
	);
	const [readFlag, setReadFlagLocal] = useState();
	// initialise the read flags from the activity
	useEffect(() => {
		const _aiDraftActivity = getLatestAIDraft(activities);
		setAiDraftActivity(_aiDraftActivity);
		setReadFlagLocal(_aiDraftActivity?.readFlag);
	}, [activities]);

	// set AI context (answer + AI draft)
	useEffect(() => {
		// set AI context
		const aiContext = {
			...(questionData || {}),
			questionId,
			questionnaireId,
			// round info
			period_start: round?.start,
			period_end: round?.end,
		};
		if (aiDraftActivity) {
			// AI draft activity
			const completeness = getStatusFromActivity("completeness", aiDraftActivity);
			const confidence = getStatusFromActivity("confidence", aiDraftActivity);
			// AI draft activity
			aiContext.AI_draft = aiDraftActivity?.newValue;
			aiContext.AI_draft_confidence = confidence;
			aiContext.AI_draft_completeness = completeness;
			const fileinfoIds = aiDraftActivity.jsonPatch
				?.filter((patch) => patch.path === "/files")
				?.map((patch) => patch.value)[0];
			aiContext.AI_draft_fileinfo_ids = fileinfoIds;
		}
		// active tab
		aiContext.activeTab = activeTabText;

		setAIContext(aiContext, thisQuestionUrl);
	}, [questionId, questionData, questionnaireId, round, aiDraftActivity, activeTabText, thisQuestionUrl]);

	/**set the answer, and do a save */
	// Note: useCalback is used here to avoid triggering hooks below on every render
	const setAIAnswer = useCallback(
		(answer, extra) => {
			// NB: extra can be mockQuestionData
			//   const {confidence, completeness, aiId,} = extra || {};
			questionData._answerParts = null; // clear out parsed version, if any
			setCurrentAnswer(answer);
			// save the answer -- this should also create an activity
			let newQuestionData = { ...questionData, ...extra };
			newQuestionData.answer = answer;
			// avoid assignees to avoid email->id mapping need
			delete newQuestionData.assignees;
			// Clear parsed parts locally in cache and optimistically update the answer
			const key = [...issbKeys.answer(), questionId];
			queryClient.setQueryData(key, (old) => {
				return newQuestionData;
			});
			questionService
				.update(questionId, newQuestionData, {
					params: { draft: false, byAI: true, success: true, aiId: extra?.aiId },
				})
				.then(() => {
					setAnswerModifiedFlag(false);
					// Refresh only the specific question and activities
					queryClient.invalidateQueries(key);
					queryClient.invalidateQueries(issbKeys.activities());
				})
				.catch((err) => {
					// revert or re-fetch on error
					showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
					queryClient.invalidateQueries(key);
				});
		},
		[questionData, questionId, queryClient, setCurrentAnswer, toast]
	);
  // This gets passed down to the AnswerActivityOneEvent component, for the Apply button.
  // Note: this sets the answer and the status e.g. completeness, confidence, etc.
  // There is a minor API / UX issue, that set-status is saved instantly -- separately from edit-answer -> save-answer, which has a manual save button.
  // TODO: Maybe move all edits into using the same API pipeline, which can send a set of edits and runs via the (save) button. This would also be nice in standardising code for fitting with usual save semantics and a CRUD-like API -- and could reduce the code paths.
  // Or/and have save auto-trigger on answer changes, removing the save button, and making all edits instantly saved.
  const applyActivity = useCallback(
    (activity) => {
      if (activity.type === "UPDATE_ANSWER") {
        // confidence and completeness
        const confidence = getStatusFromActivity("confidence", activity);
        const completeness = getStatusFromActivity("completeness", activity);
        setAIAnswer(markdownToHtml(activity.newValue), { confidence, completeness, aiId: activity.aiId });
        if (confidence) {
          handleChange("confidence", confidence);
        }
        if (completeness) {
          handleChange("completeness", completeness);
        }
      } else {
        console.error("applyActivity TODO code for type", activity);
      }
    },
    [handleChange, setAIAnswer]
  );

	// callback for if the user applies a chat-answer from the AI assistant.
	// This will 1. create an AI-draft activity, 2. save (if it can) via setAIAnswer()
	const applyAIAnswerFromChat = (event) => {
		console.log("handle applyAnswer", event);
		if (event.action !== ACTION_applyAnswer) {
			console.warn("applyAIAnswerFromChat - unexpected event action", event);
			return;
		}
		if (!event.message?.content) {
			throw new Error("applyAIAnswerFromChat - no content", event);
		}
		if (event.processed) {
			console.warn("applyAIAnswerFromChat - skip event already processed", event);
			return;	
		}
		event.processed = true; // paranoia (esp. as we only allow one AIEventListener at a time)
		// update the UI answer first
		let answer = event.message.content;
		if (event.message.json_content?.answer) {
			// for multipart answers, content was a display string - the real answer is in json
			answer = event.message.json_content.answer;
			if (typeof answer !== "string") {
				answer = JSON.stringify(answer);
			}
		}
		setCurrentAnswer(answer);
		// HACK: create an AI-draft activity for this
		let mockQuestionData = Object.assign({}, questionData, {
			answer,
			completeness: event.message.json_content?.completeness,
			confidence: event.message.json_content?.confidence,
			filesUsed: event.message.json_content?.files, // TODO test this
			sourceSlices: event.message.json_content?.sourceSlices,
		});
		let aiId = event.message.aiId;
		mockQuestionData.aiId = aiId;
		assert(mockQuestionData.answer === answer);
		console.log("applyAIAnswerFromChat - mockQuestionData", mockQuestionData);
		questionService
			.update(questionId, mockQuestionData, {
				params: { draft: true, byAI: true, aiId, success: true },
			})
			.then(() => {
				if (canSave) {
					setAIAnswer(answer, mockQuestionData);
				} else {
					queryClient.invalidateQueries(issbKeys.activities());
					showInfoToast(toast, "This AI answer has been saved as a draft in the Activity feed.");
				}
			});		
	};
	setAIEventListener(applyAIAnswerFromChat);

	// excluded by condition? true/false/unset
	//   const [conditionEvaluated, setConditionEvaluated] = useState(null);
	// ...collect all question data for the round (so that we can check conditions)
	let isCondition = !!questionData?.condition;
	if (!isCondition) {
		// are there any parts with conditions?
		isCondition = questionData?.parts?.some((part) => part.condition);
	}
	if (!isCondition) {
		// how about a part with a rowLabel that has a condition?
		questionData?.parts
			?.filter((part) => part.type === QAPartType.table)
			.forEach((qPart) => {
				const rows = toArray(qPart.rows);
				rows?.forEach((row) => {
					if (typeof row === "string") {
						row = { label: row };
					}
					if (getConditionFilter(row.label)) {
						isCondition = true;
					}
				});
			});
	}
	// ./isCondition
	// fetch data if isCondition or downstream Qs
	const { data: allQuestionData } = useGetQuestionsOfRound(null, questionData?.questionnaireId, {
		enabled: (isCondition || allLinkedRefs(questionData).length > 0) && !!questionData?.questionnaireId,
	});
	// collect question data by reference
	const [allQuestionDataByRef, setAllQuestionDataByRef] = useState(null);
	useEffect(() => {
		if (!allQuestionData) return;
		const _allQuestionDataByRef = {};
		allQuestionData.forEach((q) => {
			_allQuestionDataByRef[q.issbReference] = q;
		});
		setAllQuestionDataByRef(_allQuestionDataByRef);
	}, [allQuestionData]);

	//   // check the condition (if there is one) -- Can we rely on the server? Avoid too much overlapping code
	const conditionEvaluated =
		{
			RELEVANT: true,
			EXCLUDED: false,
			NOT_APPLICABLE: false,
			UNDETERMINED: "unset",
			UNCHECKED: "unset",
		}[questionData?.relevance] || "unset";
	//   useEffect(() => {
	//     if (!questionData?.condition) return;
	//     if (!allQuestionDataByRef) return;
	//     const result = checkCondition({ condition: questionData.condition, questionData, allQuestionDataByRef });
	// setConditionEvaluated(result);
	//   }, [questionData, allQuestionDataByRef]);

	// Save answer? Only in pre-audit stages, and if owner or assignee
	let canSave = true;
	let cannotSaveReason;
	const { isOwner } = useAirUserRoles();
	if (
		!["START", "PENDING", "AI_QUEUE", "EXPERT_REVIEW", "IN_PROGRESS", "EXCLUDED", "EXCLUDED_BY_RULE"].includes(
			questionData?.stageType
		)
	) {
		if (questionData?.stageType === "INTERNAL_REVIEW" && isOwner(questionData)) {
			canSave = true;
		} else {
			canSave = false;
			cannotSaveReason = "It is in " + (questionData?.stage || "unknown") + " stage, and not in the editing stages.";
		}
	}
	if (questionData?.relevance === "UNDETERMINED") {
		canSave = false;
		cannotSaveReason = space(cannotSaveReason, "First answer the earlier linked question to determine relevance.");
	}
	if (questionData?.relevance === "EXCLUDED" || questionData?.relevance === "NOT_APPLICABLE") {
		canSave = false;
		cannotSaveReason = space(cannotSaveReason, `This question is ${questionData.relevance} by the condition.`);
	}
	if (!isAssigned(questionData)) {
		canSave = false;
		cannotSaveReason = space(cannotSaveReason, "You are not the owner or assignee of this question.");
	}

	// Right sidebar
	// const [debug,setDebug] = useState({}); working out why this triggers too often -- done: it was setRightSidebar being altered (fixed)
	useEffect(() => {
		// const newDebug = { applyActivity, canSave, questionData, setRightSidebar };
		// const diffKeys = Object.keys(newDebug).filter(key => debug[key] !== newDebug[key]);
		// console.log("AnswerDetailPage useEffect setRightSidebar - Why has this been called?", diffKeys, debug, newDebug);
		// setDebug(newDebug);
		setRightSidebar(
			<AnswerDetailManagement questionData={questionData} applyActivity={applyActivity} canSave={canSave} />,
			true,
			"AnswerDetailManagement"
		);
		doOpen("AnswerDetailManagement");
	}, [applyActivity, canSave, doOpen, questionData, setRightSidebar]);

	/** Send a message to the AI chat — appends to existing chat if open, otherwise opens a new one */
	const sendToAIChat = useCallback((text) => {
		if (activationId?.startsWith("AIAssist")) {
			window.dispatchEvent(new CustomEvent("ai-chat-send-message", { detail: { text } }));
		} else {
			setRightSidebar(<AIAssistPanel initialInput={text} autoSend />);
			doOpen("AIAssistEdit");
		}
	}, [activationId, setRightSidebar, doOpen]);

	const handleEditWithAI = useCallback(() => {
		const draftText = aiDraftActivity?.newValue || "";
		if (activationId?.startsWith("AIAssist")) {
			sendToAIChat("Edit this draft answer:\n\n" + draftText);
		} else {
			const initialMessages = [
				{ role: "assistant", content: "Here is your current draft answer:\n\n" + draftText + "\n\nHow would you like me to edit it?" },
			];
			setRightSidebar(<AIAssistPanel initialMessages={initialMessages} />);
			doOpen("AIAssistEdit");
		}
	}, [aiDraftActivity, activationId, sendToAIChat, setRightSidebar, doOpen]);

	const handleRegenerateAnswer = useCallback(async () => {
		let roundFiles = [];
		try {
			const folder = getFolderForRound(questionnaireId);
			if (folder) {
				const filesPage = await fileinfoService.list({ size: 1000 });
				const allFiles = filesPage?.content || [];
				roundFiles = allFiles.filter(f => f.folders?.includes(folder));
			}
		} catch (e) {
			console.warn("Failed to load round sources for regenerate", e);
		}
		const count = roundFiles.length;
		const sourceNames = roundFiles.map(f => f.title || f.filename || f.id);
		let content;
		if (count > 0) {
			content = `Ready to regenerate your answer using **${count} source${count === 1 ? "" : "s"}** from this round.\n\nWould you like to:\n- **Regenerate with all sources**\n- **Exclude specific sources** — tell me which to remove\n- **Add new sources**`;
		} else {
			content = "No sources are currently available for this round. You can add sources and then regenerate.";
		}
		const initialMessages = [
			{ role: "assistant", content, roundFiles, sourceNames, roundId: questionnaireId },
		];
		setRightSidebar(<AIAssistPanel initialMessages={initialMessages} />);
		doOpen("AIAssistRegenerate");
	}, [questionnaireId, setRightSidebar, doOpen]);

	// const location = useLocation();
	const qParam = searchParams.get("q");
	// rtab=1 for the requirements list tab
	const href = modifiedUrl({ rtab: 1, q: qParam }, routes.issbRequirements);

	// TODO tags from entities
	const tags = [];

	// can only happen during navigation
	if (!questionData) {
		return <LocalSpinner />;
	}

	return (
		<SecondaryHeader>
		<MarginsWrapper questionData={questionData}>
			<Flex align="center" mb={8}>
				<AButton
					shouldConfirm={answerModifiedFlag}
					confirmConfig={{
						title: "Unsaved changes?",
						message:
							"It looks like you are navigating away with unsaved changes. Continuing will mean these changes will be lost.",
						confirmText: "Discard Changes",
						cancelText: "Cancel",
					}}
					leftIcon={<FaArrowLeft />}
					variant="link"
					color="limeShade.600"
					href={href}
				>
					<Text as="span" color="whiteShade.600">
						Requirements
					</Text>
				</AButton>
			</Flex>

			<QuestionHeader questionData={questionData} />

			{questionData?.condition && (
				<DependencyCondition condition={questionData.condition} conditionEvaluated={conditionEvaluated} />
			)}

			<Box mb={4} mt={4}>
				<Tabs
					variant="primary"
					index={activeTabIndex}
					onChange={setActiveTabIndex}
				>
					<TabList>
						<Tab title={cannotSaveReason}>Disclosure</Tab>
						{aiDraftActivity && canSave && (
							<Tab>
								<Sparkle size={16} weight="fill" style={{ marginRight: 6 }} />
								AI Draft
								<NewMarker isNew={!readFlag} />
							</Tab>
						)}
					</TabList>
					<Divider />
					<TabPanels>
						<TabPanel px={0}>
							<YourAnswerWithActionButtons
								canSave={canSave}
								cannotSaveReason={cannotSaveReason}
								setCurrentAnswer={setCurrentAnswer}
								answerModifiedFlag={answerModifiedFlag}
								questionData={questionData}
								setAnswerModifiedFlag={setAnswerModifiedFlag}
								questionId={questionId}
								questionnaireId={questionnaireId}
								allQuestionDataByRef={allQuestionDataByRef}
								tags={tags}
							/>
						</TabPanel>
						{aiDraftActivity && canSave && (
							<TabPanel px={0}>
								{activeTabIndex === 1 ? (
									<AIDraftTab
										questionId={questionId}
										questionData={questionData}
										canSave={canSave}
										activity={aiDraftActivity}
										applyActivity={applyActivity}
										isActive={activeTabIndex === 1}
										setReadFlag={(v) => setReadFlagLocal(v)}
										allQuestionDataByRef={allQuestionDataByRef}
										tags={tags}
										onEditWithAI={handleEditWithAI}
										onRegenerateAnswer={handleRegenerateAnswer}
										sendToAIChat={sendToAIChat}
									/>
								) : (
									<div />
								)}
							</TabPanel>
						)}
					</TabPanels>
				</Tabs>
			</Box>
			{/* end tabs Box */}
			<LinkedQuestions questionData={questionData} allQuestionData={allQuestionData} hasUnsavedChanges={answerModifiedFlag} />
		</MarginsWrapper>
		</SecondaryHeader>
	);
}

/**
 * // Layout HACK
  // CDP's table formats need as much space as posssible. But we want to make minimal edits to minimise the risk of issues.
  // So we sniff the question-type
  // Wide support: leave 30vw for the right sidebar
  // Older narrow column: use a grid layout with a main content area and 400px for the right sidebar
  Note: Currently, the right sidebar floats (position:fixed) outside this layout, so we allow space for it. In future the right sidebar may come into the page here, which would change this.
 */
function MarginsWrapper({ children, questionData }) {
	const isMultipart = isMultipartQuestion(questionData);
	const hasTable = isMultipart && questionData.parts.some((part) => part.type === QAPartType.table);
	if (hasTable) {
		//30vw is to match RightSidebarWidget medium-width. Then add a bit of margin (which doesnt seem to work unless it is subtracted from the width)
		return <WideMarginsWrapper>{children}</WideMarginsWrapper>;
	}
	return (
		<Grid id="AnswerDetailPage-narrow" templateColumns="1fr 400px">
			<IssbContainer margin="10%">
				<Box w="100%" maxW="900px" mx="auto" p={5}>
					{children}
				</Box>
			</IssbContainer>
		</Grid>
	);
}

export function WideMarginsWrapper({ children, sidebarWidth = "30vw", bg }) {
	return (
		<Box
			id="AnswerDetailPage-wide"
			padding-left="1%"
			padding-right="1%"
			w={`calc(100% - ${sidebarWidth} - 10px)`}
			bg={bg}
		>
			{children}
		</Box>
	);
}

/**
 * @param {Object?} questionData
 * @returns {string[]} de-duped list of all linked references (upstream and downstream)
 */
function allLinkedRefs(questionData) {
	if (!questionData) return [];
	const linkedReferences = questionData?.linkedReferences || [];
	const conditionReferences = questionData?.conditionReferences || [];
	const conditionReferencesDownstream = questionData?.conditionReferencesDownstream || [];
	const refs = [...linkedReferences, ...conditionReferences, ...conditionReferencesDownstream];
	const uniqueRefs = [...new Set(refs)];
	return uniqueRefs;
}

/**
 * Figma: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=2855-35678&t=cOxtr5gOR5XmKius-0
 *
 * Reuses style & code from the DataInputPanel -> QuestionCard
 */
function LinkedQuestions({ questionData, allQuestionData, hasUnsavedChanges}) {
	const linkedReferences = questionData?.linkedReferences || [];
	const conditionReferences = questionData?.conditionReferences || [];
	const conditionReferencesDownstream = questionData?.conditionReferencesDownstream || [];
	if (!linkedReferences.length && !conditionReferences.length && !conditionReferencesDownstream.length) return null;
	if (!allQuestionData) return null;
	const allQuestionDataByRef = {};
	allQuestionData.forEach((q) => {
		allQuestionDataByRef[q.issbReference] = q;
	});
	const linkedQuestions = linkedReferences.map((ref) => allQuestionDataByRef[ref]).filter(Boolean);
	const conditionQuestions = conditionReferences.map((ref) => allQuestionDataByRef[ref]).filter(Boolean);
	const conditionQuestionsDownstream = conditionReferencesDownstream
		.map((ref) => allQuestionDataByRef[ref])
		.filter(Boolean);
	const questions = dedupe(
		[...linkedQuestions, ...conditionQuestions, ...conditionQuestionsDownstream],
		(q) => q?.issbReference
	).filter((q) => !isExcludedFn(q));
	let naQuestionRefs = conditionReferencesDownstream.filter((ref) => !questions.find((q) => q.issbReference === ref));
	if (!questions.length && !naQuestionRefs.length) return null;
	naQuestionRefs = [...new Set(naQuestionRefs)]; // dedupe
	return (
		<Box>
			<H3>Linked Questions</H3>
			<Flex direction="column" gap={2}>
				{questions.map((q) => (
					<QuestionCard key={q.id} question={q} hasUnsavedChanges={hasUnsavedChanges}  /> //added flag for unsaved changed to be passed
				))}
				{naQuestionRefs.length > 0 && (
					<>
						<H4>Linked but not applicable</H4>
						{naQuestionRefs?.map((ref) => (
							<Text key={ref} fontSize="body.xs" mb={2} color="whiteShade.600">
								{ref}
							</Text>
						))}
					</>
				)}
			</Flex>
		</Box>
	);
}

export function QuestionHeader({ questionData }) {
	return (
		<>
			<Text fontSize="body.xs" mb={2} color="whiteShade.600">
				{questionData?.issbReference}
			</Text>

			<Heading fontSize="heading.l" color="darkTeal" mb={4}>
				<MarkdownText>{questionData?.question}</MarkdownText>
			</Heading>

			<Text fontSize="body.s" color="gray.600" mb={2}>
				{questionData?.description}
			</Text>
		</>
	);
}

/**
 * Box showing condition text -- changes colour based on conditionEvaluated
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=4646-51984&m=dev
 * @param {Object} props
 * @param {string?} props.questionReference - e.g. "1.8.1" Leave blank for "This question"
 * @param {string} props.condition - the condition text
 * @param {string|boolean} props.conditionEvaluated - the condition evaluated true/false/unset
 * @returns
 */
export function DependencyCondition({ questionReference, condition, conditionEvaluated }) {
	if (!condition) return null;
	// colors
	const sconditionEvaluated = "" + conditionEvaluated;
	let background =
		{
			true: "green.500",
			false: "yellow.100",
			unset: "gray.500",
		}[sconditionEvaluated] || "gray.500";
	let foreground = "white";
	if (sconditionEvaluated === "false") {
		foreground = "black";
	}

	let refText = questionReference ? `Question ${questionReference}` : "This question";

	// make the condition text a little bit more readable
	let conditionText = prettyPrintCondition(condition);

	return (
		<Box mb={4} mt={4} backgroundColor={background} color={foreground} p={4} rounded={8}>
			<Stack direction="row">
				<LinkIcon color={foreground} size={24} />
				<Box flex="1">
					<H4 color={foreground}>Dependency {questionReference}</H4>
					<Text color={foreground}>
						{refText} only applies if:
						{conditionText}
					</Text>
				</Box>
			</Stack>
		</Box>
	);
}

function NewMarker({ isNew }) {
	// some animation for when this goes away might be nice
	if (!isNew) return null;
	return (
		<Badge bg="red.500" borderRadius="sm" color="white" pl={2} pr={2} ml={2}>
			New
		</Badge>
	);
}

/**
 * check parts and sub-parts for any type=file part -- in which case the question must allow attachments
 * @param {QAPart[]} parts
 * @returns {boolean}
 */
function findFilePart(parts) {
	if (!parts) return false;
	for (const part of parts) {
		if (part.type === QAPartType.file) {
			return true;
		}
		if (part.type === QAPartType.table) {
			if (findFilePart(part.columns)) {
				return true;
			}
		}
	}
	return false;
}

function YourAnswerWithActionButtons({
	questionId,
	canSave,
	cannotSaveReason,
	setCurrentAnswer,
	answerModifiedFlag,
	questionData,
	setAnswerModifiedFlag,
	questionnaireId,
	allQuestionDataByRef,
	tags,
}) {
	const { isAuditor, isESGAdmin } = useAirUserRoles();
	const toast = useToast();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const { setRightSidebar, doOpen, activationId } = useRightSidebar();
	const { prompts: disclosurePrompts } = useContextualPrompts({
		location: "disclosure",
		stage: questionData?.stageType,
		completeness: questionData?.completeness,
		confidence: questionData?.confidence,
	});

	/** Send a prompt to the AI chat — appends to existing chat if open, otherwise opens a new one */
	const sendToAIChat = useCallback((text) => {
		if (activationId?.startsWith("AIAssist")) {
			window.dispatchEvent(new CustomEvent("ai-chat-send-message", { detail: { text } }));
		} else {
			setRightSidebar(<AIAssistPanel initialInput={text} autoSend />);
			doOpen("AIAssistEdit");
		}
	}, [activationId, setRightSidebar, doOpen]);

	const { mutate: saveAnswer } = useSaveAnswer({
		onMutate: () => dispatch(showAppSpinner()),
		onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
		onSettled: () => dispatch(hideAppSpinner()),
		onSuccess: (result) => {
			setAnswerModifiedFlag(false);
			// keep this to auto-refresh the question data, so that it will reflect latest changes (stages, etc)
			queryClient.invalidateQueries(issbKeys.answer());
			if (result?.sideEffectIds?.length) {
				// invalidate the q list - to update linked questions
				queryClient.invalidateQueries(issbKeys.questionList());
				// TODO make this work
				showInfoToast(toast, `This answer has affected ${result.sideEffectIds.length} other questions.`);
			}
		},
	});

	const handleSaveAnswer = () => {
		const dataToSubmit = {
			questionId: questionId,
			answer: questionData.answer,
			files: questionData?.files,
		};
		saveAnswer(dataToSubmit);
	};

	// Files
	const fileFolderForRound = getFolderForRound(questionnaireId);
	const fileInfoIds = questionData?.files;
	const [fileInfoList, setFileInfoList] = React.useState();
	// initialise fileInfoList by loading from FileInfo[]s from backend
	useEffect(() => {
		if (!fileInfoIds) return;
		if (fileInfoList) return;
		const promises = fileInfoIds.map((id) => fileinfoService.get(id));
		Promise.all(promises).then((fileInfos) => {
			setFileInfoList(fileInfos);
		});
	}, [fileInfoIds, fileInfoList]);
	// called when the user adds/removes a file
	const onFileInfoListChange = (fileInfoList) => {
		setFileInfoList(fileInfoList);
		questionData.files = fileInfoList.map((f) => f.id);
		setAnswerModifiedFlag(true);
	};

	const roundSelector = useSelector((state) => state.issbRound);
	const round = roundSelector.selectedRound;
	let offerAttachments = round?.answersCanHaveAttachments !== false; // TODO also if a q part has type=file
	if (!offerAttachments && isMultipartQuestion(questionData)) {
		offerAttachments = findFilePart(questionData.parts);
	}
	const canUseHtml = round?.answersCanUseHtml !== false;

	const handleShowDownstreamQuestions = () => { };

	assert(
		!questionData.answer || typeof questionData.answer === "string",
		"YourAnswerWithActionButtons answer must be a string",
		questionData.answer
	);

	// When to show control objective / guidance notes?
	// HACK: this is a pragmatic setup, which could probably be simplified by allowing more people to see the notes
	const showQuestionSpec = isAuditor(questionData) || isESGAdmin || isCDPQuestion(questionData);

	return (
		<Flex direction="column" gap={2}>
			{/* TODO a popup to show the downstream question DependencyConditions questionData.conditionReferencesDownstream?.length &&  
	<Box align="right">
	 	<div style={{ cursor: "pointer" }} onClick={handleShowDownstreamQuestions}>
			<LinkIcon size={24} />
		 </div>
	 </Box>
	*/}
			<Box
				border="1px solid"
				borderColor="gray.200"
				borderRadius="xl"
				bg="white"
				overflow="hidden"
			>
				{/* Answer content */}
				{isMultipartQuestion(questionData) ? (
					<Box px={5} py={4}>
						<QuestionPartsList
							questionData={questionData}
							setAnswer={setCurrentAnswer}
							canUseHtml={canUseHtml}
							readOnly={!canSave}
							fileInfoList={fileInfoList}
							allQuestionDataByRef={allQuestionDataByRef}
							tags={tags}
							reason={cannotSaveReason}
						/>
					</Box>
				) : (
					<Box sx={{ "& > div": { border: "none", borderRadius: 0 } }}>
						<RichTextEditor
							content={questionData.answer || ""}
							onChange={(html) => setCurrentAnswer(html)}
							isDisabled={!canSave}
						/>
					</Box>
				)}

				{/* Edit section — contextual prompts from rules system */}
				{disclosurePrompts.length > 0 && (
					<>
						<Divider borderColor="gray.200" />
						<Box px={5} py={3}>
							<HStack spacing={2} mb={3}>
								<Sparkle size={16} weight="fill" color={colors.limeShade[600]} />
								<Text fontSize="body.s" fontWeight="semibold" color="limeShade.600">
									{questionData.answer ? "Edit" : "Draft"}
								</Text>
							</HStack>
							<Flex gap={2} flexWrap="wrap">
								{disclosurePrompts.map((p) => (
									<Button
										key={p.id}
										variant="outline"
										size="sm"
										borderRadius="full"
										borderColor="gray.300"
										color="gray.600"
										fontWeight="normal"
										bg="white"
										_hover={{ borderColor: "headingLime", color: "darkTeal" }}
										onClick={() => sendToAIChat(p.content)}
									>
										{p.name}
									</Button>
								))}
							</Flex>
						</Box>
					</>
				)}
			</Box>

			{offerAttachments && (
				<Flex gap={4} mb={4} align="start">
					<Box flex="1">
						<FileListWithBrowseOrUpload
							fileLabel="reference file"
							readOnly={!canSave}
							onFileInfoListChange={onFileInfoListChange}
							fileInfoList={fileInfoList}
							tags={tags}
							folder={fileFolderForRound}
						/>
					</Box>
				</Flex>
			)}

			{showQuestionSpec && <QuestionSpec questionData={questionData} />}

			{canSave ? (
				<Button
					mt={8}
					variant="primary"
					w="100%"
					onClick={() => handleSaveAnswer()}
					isDisabled={!answerModifiedFlag}
					title={answerModifiedFlag ? "Save" : "No changes to save"}
				>
					Save
				</Button>
			) : null}
			<AnswerActionButtons
				questionData={questionData}
				questionId={questionData.id}
				currentStageType={questionData?.stageType}
				currentStageName={questionData?.stage}
				answerModifiedFlag={answerModifiedFlag}
			/>
		</Flex>
	);
} // ./YourAnswerWithActionButtons

export function QuestionSpec({ questionData }) {
	return (
		<Accordion allowToggle>
			{questionData?.controlDescription && (
				<AccordionItem py={4} borderColor="gray.400">
					<AccordionButton title="Guidance notes on how the answer should be approached">
						<Box flex="1" textAlign="left" fontWeight="bold" color="teal">
							Control Description
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel>
						<MarkdownText fontSize="body.xs">{questionData?.controlDescription}</MarkdownText>
					</AccordionPanel>
				</AccordionItem>
			)}
			{!questionData?.controlDescription && questionData?.controlObjective && (
				<AccordionItem py={4} borderColor="gray.400">
					<AccordionButton title="The specification text">
						<Box flex="1" textAlign="left" fontWeight="bold" color="teal">
							{/** HACK */ isCDPQuestion(questionData) ? "Guidance Notes" : "Control Objective"}
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel>
						<MarkdownText fontSize="body.xs">{questionData?.controlObjective}</MarkdownText>
					</AccordionPanel>
				</AccordionItem>
			)}
			{questionData?.evidenceOfControl && (
				<AccordionItem py={4} borderColor="gray.400">
					<AccordionButton title="Recommended evidence that should be provided to support the answer">
						<Box flex="1" textAlign="left" fontWeight="bold" color="teal">
							Evidence of Control
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel pb={4}>
						<MarkdownText fontSize="body.xs">{questionData?.evidenceOfControl}</MarkdownText>
					</AccordionPanel>
				</AccordionItem>
			)}
		</Accordion>
	);
}
